import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from './supabase';
import { isCell } from './cell/config';
import { listen, myLocations, TOPIC_OF_TABLE } from './cell/realtime';

/**
 * Subscribe to Supabase Realtime changes on `table` and invalidate the
 * given react-query keys when any row changes.
 *
 * Per WI-114: the Hub uses react-query with staleTime: 0 as a safety
 * net, but Pro and Owner edit the same records concurrently — staleness
 * should be measured in seconds, not seconds-since-tab-focus. Realtime
 * push closes that gap so a Pro tab and an iOS Owner app stay in sync
 * without anyone tabbing away and back.
 *
 * Pair with `filter` (e.g. `pro_id=eq.<uuid>`) on every call to keep
 * subscriptions tightly scoped — RLS is enforced on the channel anyway,
 * but a server-side filter is cheaper and avoids waking the client up
 * for rows it would just drop.
 *
 * Reconnect behavior: the SDK auto-reconnects after a network blip, but
 * does NOT replay events missed while disconnected. Invalidating the
 * query keys on every event (including the synthetic ones the SDK emits
 * after re-subscribe) makes us re-fetch and back-fill anything missed.
 */
export function useRealtimeTable(opts: {
  table: string;
  /** Server-side filter, e.g. `pro_id=eq.<uuid>`. Omit only for tables
   *  with very low write volume where RLS-only filtering is acceptable. */
  filter?: string;
  /** react-query keys to invalidate when a change arrives. Each entry
   *  is passed to `queryClient.invalidateQueries({ queryKey })`. */
  invalidateKeys: ReadonlyArray<ReadonlyArray<unknown>>;
  /** When false (e.g. waiting on a pro_id lookup), the hook is a no-op. */
  enabled?: boolean;
  /** Optional override for the channel name. Defaults to a unique name
   *  composed from table + filter so two components subscribing to
   *  different filters on the same table don't share a channel. */
  channelName?: string;
}) {
  const { table, filter, invalidateKeys, enabled = true, channelName } = opts;
  const qc = useQueryClient();

  // Stable keys array for the dependency check below. JSON.stringify is
  // fine here — keys are short arrays of primitives by convention.
  const keysSig = JSON.stringify(invalidateKeys);

  useEffect(() => {
    if (!enabled) return;

    if (isCell()) {
      // CELL (M28): AppSync Events channels per studio (lib/cell/realtime.ts). The Supabase filter becomes: the studio
      // named by `location_id=eq.X`, or every studio the token names with a client-side match on the notice's key
      // (`pro_id=eq.X`, carried on ticket and spotlight notices). A table that was never published stays silent.
      if (!TOPIC_OF_TABLE[table]) return;
      const m = /^(\w+)=eq\.(.+)$/.exec(filter ?? '');
      const invalidate = () => { for (const key of invalidateKeys) qc.invalidateQueries({ queryKey: key as unknown[] }); };
      const offs: Array<() => void> = [];
      let live = true;
      void (async () => {
        const locs = m && m[1] === 'location_id' ? [m[2]] : await myLocations();
        if (!live) return;
        for (const loc of locs) {
          offs.push(listen(loc, table, (n) => {
            if (n.op === 'resubscribe') return invalidate();
            if (m && m[1] !== 'location_id' && n[m[1]] !== undefined && String(n[m[1]]) !== m[2]) return;
            invalidate();
          }));
        }
      })();
      return () => { live = false; offs.forEach((f) => f()); };
    }

    // Supabase Realtime — production only (CELL (M28) returned above).
    const name = channelName ?? `rt:${table}:${filter ?? '*'}`;
    const channel = supabase
      .channel(name)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table, ...(filter ? { filter } : {}) },
        () => {
          for (const key of invalidateKeys) {
            qc.invalidateQueries({ queryKey: key as unknown[] });
          }
        },
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); }; // CELL (M28): production only
    // invalidateKeys is intentionally not in the dep list — `keysSig`
    // captures structural equality and prevents tearing down the
    // subscription every render when callers pass an inline array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, table, filter, channelName, qc, keysSig]);
}
